"use client";

import {useCallback, useEffect, useMemo, useState} from "react";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {z} from "zod";
import {MutationProposalReviewContext, MutationProposalSideConversationContext, type MutationProposalReview} from "@/contexts/chat/MutationProposalReviewContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {MutationProposalOutcomesProvider} from "@/contexts/chat/MutationProposalOutcomesContext";
import {reviewEditorPage} from "@/services/wingman/mutation-proposal";

const uuid = z.string().uuid();

/**
 * Reload the authenticated conversation, never reconstruct a proposal from today's target.
 * The provider is always mounted so ending a review (dropping its query) never remounts
 * the page or the dock beneath it, and the conversation stays beside the target it just
 * changed until the person leaves that page.
 */
export default function MutationProposalPage({children}: {children: React.ReactNode}) {
   const search = useSearchParams();
   const pathname = usePathname();
   const router = useRouter();
   const {isDockAvailable, showConversationOnRight} = useGailDock();
   const [host, setHost] = useState<HTMLDivElement | null>(null);
   const [hasReview, setHasReview] = useState(false);
   const [decisionPending, setDecisionPending] = useState(true);
   const [reviewed, setReviewed] = useState<{targetId: string; conversationId: string} | null>(null);
   const proposalId = search.get("proposalId");
   const conversationId = search.get("conversationId");
   const page = useMemo(() => reviewEditorPage(pathname), [pathname]);
   // Script proposals are reviewed inline in chat, without a second conversation in the editor.
   const target = useMemo(() => page?.capability === "use_cases_update" && uuid.safeParse(proposalId).success && uuid.safeParse(conversationId).success
      ? {...page, proposalId: proposalId!, conversationId: conversationId!} : null, [page, proposalId, conversationId]);
   const finish = useCallback(() => {
      if (!target) return;
      setReviewed({targetId: target.targetId, conversationId: target.conversationId});
      showConversationOnRight(target.conversationId);
      const query = new URLSearchParams(search.toString());
      query.delete("proposalId");
      query.delete("conversationId");
      const queryString = query.size ? `?${query}` : "";
      router.replace(`${pathname}${queryString}`, {scroll: false});
   }, [target, pathname, search, router, showConversationOnRight]);
   const reviewConversationId = target?.conversationId;
   useEffect(() => {
      if (isDockAvailable && reviewConversationId) showConversationOnRight(reviewConversationId);
   }, [isDockAvailable, reviewConversationId, showConversationOnRight]);
   const review = useMemo<MutationProposalReview | null>(() => target && {...target, host, setHost, hasReview, setHasReview, decisionPending, setDecisionPending, finish},
      [target, host, hasReview, decisionPending, finish]);
   const sideConversationId = target?.conversationId ??
      (page?.capability === "use_cases_update" && reviewed?.targetId === page.targetId ? reviewed.conversationId : null);
   return <MutationProposalOutcomesProvider>
      <MutationProposalReviewContext.Provider value={review}>
         <MutationProposalSideConversationContext.Provider value={sideConversationId}>{children}</MutationProposalSideConversationContext.Provider>
      </MutationProposalReviewContext.Provider>
   </MutationProposalOutcomesProvider>;
}
